import { useMemo, useState } from 'react';

import {
    useGetSitesPaginatedQuery,
    useGetProductPriceRangesQuery,
    SitesFilterInput,
    SiteState,
    TotadminPermission,
    useSitesUpdatedSubscription,
} from 'data/__generated__';

import { DataTable } from 'components/DataTable';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

import { sitesUpdatedHandler } from '../utils/sitesUpdatedHandler';
import { getSitesColumns, type SiteRow } from '../constants/sitesColumns';
import { AssignSitesToZonePopup } from '../Popups/AssignSitesToZonePopup';

export const SitesList = () => {
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const [isAssignPopupOpen, setIsAssignPopupOpen] = useState<boolean>(false);

    const defaultFilters: SitesFilterInput = {
        state: [SiteState.Regular],
        shouldOperateOnTotops: true,
    };

    const {
        loading: sitesLoading,
        data: sitesData,
        error: sitesError,
        refetch,
        fetchMore,
    } = useGetSitesPaginatedQuery({
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        variables: { filters: defaultFilters },
    });

    const { data: priceRangesData } = useGetProductPriceRangesQuery();

    const priceRanges = priceRangesData?.productPriceRanges || [];

    // Create a map of price ranges for quick access
    const priceRangeMap = useMemo(
        () =>
            priceRanges.reduce(
                (acc, range) => {
                    acc[range._id] = range.name;
                    return acc;
                },
                {} as Record<string, string>,
            ),
        [priceRanges],
    );

    const columns = useMemo(() => getSitesColumns(priceRangeMap), [priceRangeMap]);

    useSitesUpdatedSubscription({
        onData: ({ data: { data } }) => {
            if (data?.siteUpdated) {
                sitesUpdatedHandler(data.siteUpdated);
            }
        },
    });

    if (sitesError) {
        throw new Error('Une erreur est survenue lors de la récupération des sites');
    }

    const hasMore = sitesData?.sitesFilter.hasMore ?? false;
    const sites = sitesData?.sitesFilter.sites ?? [];

    return (
        <div className="flex h-full w-full flex-col overflow-hidden">
            <DataTable<SiteRow, SitesFilterInput>
                columns={columns}
                data={sites}
                filterMode="server"
                getRowId={(row) => row._id}
                getRowLink={(row) => `/site/${row._id}`}
                hasMore={hasMore}
                isLoading={sitesLoading}
                refetch={async (vars) => {
                    await refetch({ ...vars });
                }}
                fetchMore={async (opts) => {
                    await fetchMore({ variables: opts.variables });
                }}
                defaultFilters={defaultFilters}
                searchPlaceholder="Nom du site ou de l'organisation"
                searchFilterKey="searchValue"
                enableSelection
                selectedKeys={selectedKeys}
                onSelectionChange={setSelectedKeys}
                toolbarActions={
                    selectedKeys.length > 0 ? (
                        <TotemPrimaryButton
                            onClick={() => setIsAssignPopupOpen(true)}
                            requiredPermissions={[
                                TotadminPermission.DeliveryZonesManage,
                                TotadminPermission.DeliveryZonesRead,
                            ]}
                        >
                            Affecter à une zone
                        </TotemPrimaryButton>
                    ) : null
                }
            />
            <AssignSitesToZonePopup
                isOpen={isAssignPopupOpen}
                setIsOpen={setIsAssignPopupOpen}
                siteIds={selectedKeys}
                onAssigned={async () => {
                    setSelectedKeys([]);
                    await refetch();
                }}
            />
        </div>
    );
};
